import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Pencil, X } from 'lucide-react';
import { formatCurrency } from '../../utils';
import { getCurrentLang } from '../../i18n/i18n';
import { formatSignedDiscount } from './signedAmount';
import { ReservationSummaryState } from './useReservationSummary';

interface DiscountEditRowProps {
    summary: ReservationSummaryState;
    /** Effective total — upper bound for the discount and the error/title range. */
    total: number;
    /** Lower bound (0 = discount only, negative = surcharge allowed). */
    minDiscount: number;
    /** Whether the discount can be edited (create / edit flows) or is read-only (detail). */
    editable: boolean;
}

/**
 * Shared discount row for the venue / room price boxes ("twins"). Read-only mode
 * shows the signed discount; editable mode shows the inline input with confirm /
 * cancel, or the committed value with a pencil affordance.
 */
function DiscountEditRow({ summary, total, minDiscount, editable }: Readonly<DiscountEditRowProps>) {
    const { t } = useTranslation();
    const {
        editingDiscount,
        setEditingDiscount,
        tempDiscount,
        setTempDiscount,
        numericTempDiscount,
        isDiscountValid,
        confirmDiscount,
        cancelDiscount,
    } = summary;

    if (!editable) {
        return (
            <div className={'flex flex-col justify-between text-sm items-center min-h-[32px]'}>
                <div className={'flex items-center gap-2 w-full'}>
                    <div>{t('booking_details.reservation_summary.coupon_applied')}</div>
                    <div className={'ml-auto flex items-center gap-2'}>
                        <span>{formatSignedDiscount(summary.committedDiscount)}</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={'flex flex-col justify-between text-sm items-center min-h-[32px]'}>
            {editable && !isDiscountValid && (
                <p className={'text-red-500 text-xs mt-1'}>
                    {t('booking_details.reservation_summary.discount_error', {
                        min: formatSignedDiscount(minDiscount < 0 ? -minDiscount : minDiscount),
                        max: formatCurrency(total, getCurrentLang()),
                        defaultValue: 'The discount amount must be between {{min}} and {{max}}.',
                    })}
                </p>
            )}
            <div className={'flex items-center gap-2 w-full'}>
                <div>{t('booking_details.reservation_summary.coupon_applied')}</div>
                <div className={'ml-auto flex items-center gap-2'}>
                    {editingDiscount ? (
                        <>
                            <input
                                type={'number'}
                                className={`
                                    w-20 px-2 py-0.5 border rounded text-right text-sm h-[28px] appearance-none
                                    [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none
                                    ${isDiscountValid ? '' : 'border-red-500 bg-red-50'}
                                `}
                                value={tempDiscount}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    // Allow empty string, negative sign, and numbers
                                    if (value === '' || value === '-' || /^-?\d*(?:\.\d*)?$/.test(value)) {
                                        setTempDiscount(value);
                                    }
                                }}
                                max={total}
                                min={minDiscount}
                                title={t('booking_details.reservation_summary.discount_input_title', {
                                    min: formatCurrency(minDiscount, getCurrentLang()),
                                    max: formatCurrency(total, getCurrentLang()),
                                    defaultValue: 'Discount: {{min}} to {{max}}',
                                })}
                            />
                            <button
                                onClick={confirmDiscount}
                                className={`${
                                    isDiscountValid ? 'text-green-600 hover:text-green-800' : 'text-gray-400 cursor-not-allowed'
                                } h-[28px] flex items-center`}
                                disabled={!isDiscountValid}
                                title={
                                    isDiscountValid
                                        ? t('booking_details.reservation_summary.save_discount', { defaultValue: 'Save discount' })
                                        : t('booking_details.reservation_summary.discount_invalid', {
                                            defaultValue: 'Discount must be within valid range',
                                        })
                                }
                            >
                                <Check size={18} />
                            </button>
                            <button
                                onClick={cancelDiscount}
                                className={'text-red-600 hover:text-red-800 h-[28px] flex items-center'}
                                title={t('common.cancel')}
                            >
                                <X size={18} />
                            </button>
                        </>
                    ) : (
                        <>
                            <span className={'text-sm text-right h-[28px] leading-[28px] inline-block'}>
                                {formatSignedDiscount(numericTempDiscount)}
                            </span>
                            <button
                                onClick={() => setEditingDiscount(true)}
                                className={'text-gray-500 hover:text-black h-[28px] flex items-center'}
                            >
                                <Pencil size={16} />
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default DiscountEditRow;
